import { afterNextRender, Component, ElementRef, EventEmitter, inject, Output, ViewChild } from '@angular/core';
import { FormControl, FormGroup, ReactiveFormsModule } from '@angular/forms';
import { MatInputModule } from '@angular/material/input';
import { DealerComponentState } from '../../shared/state-interface';
import { CountriesComponent } from '../../ui-components/countries/countries.component';
import { Dealer } from './dealer.model';

@Component({
    selector: 'app-dealer-component',
    standalone: true,
    imports: [
        ReactiveFormsModule,
        MatInputModule,
        CountriesComponent
    ],
    templateUrl: './dealer.component.html',
    styleUrl: './dealer.component.css'
})
export class DealerComponent {

    @ViewChild(CountriesComponent) countriesComponent!:CountriesComponent;
    @Output() messageEvent = new EventEmitter<string>();

    private elementRef: ElementRef = inject(ElementRef);

    public constructor() {
        afterNextRender(() => {
            // This LifeCycle hook primarily supports the "Add" Use Case and
            // the browser back button Use Case. The setTimeout is necessary
            // to put these commands at the end of the event queue in attempt
            // to delay their invocation until the DOM is fully updated. Same
            // functions are invoked from the populateFormFromModel function
            // to support the "Update" Use Case.
            setTimeout(() => {
                const ref = this.elementRef.nativeElement.querySelector("input");
                ref?.focus();
                ref?.select();
            }, 0);
        });
    }

    protected dealerForm = new FormGroup({
        name: new FormControl(""),
        contactFirstName: new FormControl(""),
        contactLastName: new FormControl(""),
        contactEmail: new FormControl(""),
        contactPhone: new FormControl(""),
        addressLine1: new FormControl(""),
        addressLine2: new FormControl(""),
        addressLine3: new FormControl("")
    })

    public getState(): DealerComponentState {
        const state: DealerComponentState = {} as DealerComponentState
        state.dealerForm = this.dealerForm;
        state.countriesComponentState = this.countriesComponent.getState();
        return state;
    }

    public restoreState(state: DealerComponentState) {
        this.dealerForm = state.dealerForm;
        this.countriesComponent.restoreState(state.countriesComponentState);
    }

    public init(countryCode?: string) {
        this.countriesComponent.countryCode = countryCode;
        this.countriesComponent.init();
    }

    public receiveMessage(value: string) {
        this.messageEvent.emit(value);
    }

    public populateFormFromModel(dealer: Dealer) {
        this.nameCtl.setValue(dealer.name);
        this.contactFirstNameCtl.setValue(dealer.primary_contact_first_name);
        this.contactLastNameCtl.setValue(dealer.primary_contact_last_name);
        this.contactEmailCtl.setValue(dealer.primary_contact_email);
        this.contactPhoneCtl.setValue(dealer.primary_contact_phone);
        this.addressLine1Ctl.setValue(dealer.address_line_1);
        this.addressLine2Ctl.setValue(dealer.address_line_2);
        this.addressLine3Ctl.setValue(dealer.address_line_3 ? dealer.address_line_3 : "");
        this.countriesComponent.countryCode = dealer.country_code;

        const ref = this.elementRef.nativeElement.querySelector("input");
        ref?.focus();
        ref?.select();
    }

    public createModelFromForm(dealer: Dealer): Dealer {
        dealer.name = this.nameCtl.value;
        dealer.primary_contact_first_name = this.contactFirstNameCtl.value;
        dealer.primary_contact_last_name = this.contactLastNameCtl.value;
        dealer.primary_contact_email = this.contactEmailCtl.value;
        dealer.primary_contact_phone = this.contactPhoneCtl.value;
        dealer.address_line_1 = this.addressLine1Ctl.value;
        dealer.address_line_2 = this.addressLine2Ctl.value;
        dealer.address_line_3 = this.addressLine3Ctl.value;
        dealer.country_code = this.countriesComponent.selectedCountry;
        return dealer;
    }

    private get nameCtl(): FormControl {
        return this.dealerForm.controls.name;
    }

    private get contactLastNameCtl(): FormControl {
        return this.dealerForm.controls.contactLastName;
    }

    private get contactFirstNameCtl(): FormControl {
        return this.dealerForm.controls.contactFirstName;
    }

    private get contactEmailCtl(): FormControl {
        return this.dealerForm.controls.contactEmail;
    }

    private get contactPhoneCtl(): FormControl {
        return this.dealerForm.controls.contactPhone;
    }

    private get addressLine1Ctl(): FormControl {
        return this.dealerForm.controls.addressLine1;
    }

    private get addressLine2Ctl(): FormControl {
        return this.dealerForm.controls.addressLine2;
    }

    private get addressLine3Ctl(): FormControl {
        return this.dealerForm.controls.addressLine3;
    }
}